import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CredentialsService } from '@core/services/credentials.service';
import { Observable, Subject, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  AddAddressRequest,
  PickupDropoffAddress,
} from '@app/@core/models/address.model';
import {
  SendOtpResponse,
  VerifyOtpResponse,
} from '@app/@core/models/otp.model';
import { AvailableAppCredit } from '@app/profile/profile.types';

export interface DeletionRequest {
  status: 'Pending' | string;
  requestedAtUtc: string;
  coolOffUntilUtc: string;
  expiresAtUtc: string;
  canConfirmNow: boolean;
}

export interface FaqQuestion {
  question: string;
  answer: string;
}

export interface FaqCategory {
  title: string;
  questions?: FaqQuestion[];
}

export interface IdType {
  id: number;
  name: string;
}

export interface ProfileDetails {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  gender?: number;
  phone: string;
  email: string;
  imageUrl?: string;
  nationality?: { id: number; name: string };
  identification?: {
    typeId: number;
    number: string;
    type?: { name: string };
  };
  deletionRequest?: DeletionRequest;
  availableAppCredit?: AvailableAppCredit;
}

export interface UpdateProfileRequest {
  Id: number;
  firstName: string;
  lastName: string;
  gender: number;
  email: string;
  phone: string;
  preferredLanguageCode: string;
  imageUrl: string;
  nationalityId: number;
  identification: {
    number: string;
    typeId: number;
  };
  dateOfBirth?: string;
  dateofBirthUnix?: number;
}

export interface ProfileImageLinkRequest {
  link: string;
}

export interface ProfileImageResponse {
  imageUrl: string;
}

export interface DeleteEligibility {
  isEligible: boolean;
  blockingReasonCodes?: string[];
}

export interface ChangeContactVerifyResponse {
  token: string;
}

/**
 * Raw values coming out of `CustomerDetailsComponent.getValues()` /
 * `FormGroup.getRawValue()` for the shared address form used by both the
 * address book list (edit flow) and the add-address dialog (create flow).
 */
export interface AddressFormRawValue {
  tagLocationName?: string;
  CustomerPhone?: string;
  CustomerLatitude?: number | string;
  CustomerLongitude?: number | string;
  CustomerAddressTypeId?: number;
  Area?: string;
  Building?: string;
  Floor?: string;
  Unit?: string;
  CustomerAddressNote?: string;
  nationalAddress?: string;
}

/** Body shape sent to the address create/update endpoints. */
export interface AddressRequestPayload {
  name: string;
  phone: string;
  latitude: number | string;
  longitude: number | string;
  addressType: number;
  area: string;
  building: string;
  floor: string;
  unit: string;
  note: string;
  nationalAddress: string;
}

const routes = {
  getfaqs: () => `/org/sgs/faq`,
  getProfileDetails: () => `/org/sgs/profile`,
  updateProfileDetails: () => `/org/sgs/profile`,
  getAddresses: () => `/org/sgs/address`,
  updateAddress: (id: number) => `/org/sgs/address/${id}`,
  addAddress: () => `/org/sgs/address`,
  deleteAddress: (id: number) => `/org/sgs/address/${id}`,
  getListOfCountries: () => `/org/sgs/lookup/countries`,
  getIdTypes: () => `/org/sgs/lookup/identifications`,
  uploadProfilePicture: () => `/org/sgs/attachments/add`,
  uploadProfileImg: () => `/org/sgs/profile/picture-link`,
  removeProfileImg: () => `/org/sgs/profile/picture-link`,
  sendOtp: () => `/org/sgs/profile/otps-sms`,
  verifyOtp: () => `/org/sgs/profile/otp-sms-verifications`,
  removeUserProfile: () => `/org/sgs/profile`,
  deactivateAccount: () => `/org/sgs/profile/deactivate`,
  getDeleteEligibility: () => `/org/sgs/profile/delete-eligibility`,
  requestPermanentDelete: () => `/org/sgs/profile/delete-request`,
  confirmPermanentDelete: () => `/org/sgs/profile/delete-confirm`,
  changeEmailSendOtp: () => `/org/sgs/profile/change-email/send-otp`,
  changeEmailVerify: () => `/org/sgs/profile/change-email/verify`,
  changeEmailConfirm: () => `/org/sgs/profile/change-email/confirm`,
  changePhoneSendOtp: () => `/org/sgs/profile/change-phone/send-otp`,
  changePhoneVerify: () => `/org/sgs/profile/change-phone/verify`,
  changePhoneConfirm: () => `/org/sgs/profile/change-phone/confirm`,
  changePassword: () => `/org/sgs/profile/change-password`,
};

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private profileDetails?: ProfileDetails;
  profileImageUpdated$ = new Subject<string | null>();

  constructor(
    private httpClient: HttpClient,
    private credentialsService: CredentialsService,
  ) {}

  getfaqs(): Observable<FaqCategory[]> {
    return this.httpClient.get<FaqCategory[]>(routes.getfaqs(), {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        'Cache-Control': 'no-cache',
      }),
    });
  }

  getProfileDetails(
    disabledCache: boolean = false,
  ): Observable<ProfileDetails> {
    if (!disabledCache && this.profileDetails) return of(this.profileDetails);
    return this.httpClient
      .get<ProfileDetails>(routes.getProfileDetails(), {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      })
      .pipe(
        tap((data: ProfileDetails) => {
          this.profileDetails = data;
        }),
      );
  }

  updateProfileDetails(
    profile: UpdateProfileRequest,
  ): Observable<ProfileDetails> {
    return this.httpClient.put<ProfileDetails>(
      routes.updateProfileDetails(),
      profile,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  getAddresses(): Observable<PickupDropoffAddress[]> {
    return this.httpClient.get<PickupDropoffAddress[]>(routes.getAddresses(), {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        'Cache-Control': 'no-cache',
      }),
    });
  }

  updateAddress(
    address: Partial<PickupDropoffAddress> & { id: number },
  ): Observable<PickupDropoffAddress> {
    return this.httpClient.put<PickupDropoffAddress>(
      routes.updateAddress(address.id),
      address,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  addAddress(address: AddAddressRequest): Observable<PickupDropoffAddress> {
    return this.httpClient.post<PickupDropoffAddress>(
      routes.addAddress(),
      address,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  deleteAddress(addressId: number) {
    return this.httpClient.delete(routes.deleteAddress(addressId), {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        'Cache-Control': 'no-cache',
      }),
    });
  }

  getListOfCountries() {
    return this.httpClient.get(routes.getListOfCountries(), {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        'Cache-Control': 'no-cache',
      }),
    });
  }

  getIdTypes(): Observable<IdType[]> {
    return this.httpClient.get<IdType[]>(routes.getIdTypes(), {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        'Cache-Control': 'no-cache',
      }),
    });
  }

  uploadProfilePicture(body: FormData) {
    return this.httpClient.post(routes.uploadProfilePicture(), body, {
      headers: new HttpHeaders({
        'x-ms-blob-type': 'BlockBlob',
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        'Cache-Control': 'no-cache',
      }),
    });
  }

  uploadProfileImg(
    body: ProfileImageLinkRequest,
  ): Observable<ProfileImageResponse> {
    return this.httpClient.put<ProfileImageResponse>(
      routes.uploadProfileImg(),
      body,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Content-Type': 'application/json',
        }),
      },
    );
  }

  removeProfileImg() {
    return this.httpClient.delete(routes.removeProfileImg(), {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        'Cache-Control': 'no-cache',
      }),
    });
  }

  sendOtp(phone: { phoneNumber: string }): Observable<SendOtpResponse> {
    return this.httpClient.post<SendOtpResponse>(routes.sendOtp(), phone, {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        'Cache-Control': 'no-cache',
      }),
    });
  }

  verifyOtp(code: string, phoneNumber: string): Observable<VerifyOtpResponse> {
    return this.httpClient.post<VerifyOtpResponse>(
      routes.verifyOtp(),
      {
        phoneNumber,
        otp: code,
        valid: true,
      },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  removeUserProfile() {
    return this.httpClient.delete(routes.removeUserProfile(), {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.credentialsService.credentials.token}`,
        'Cache-Control': 'no-cache',
      }),
    });
  }

  deactivateAccount(): Observable<void> {
    return this.httpClient.post<void>(
      routes.deactivateAccount(),
      {},
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  getDeleteEligibility(): Observable<DeleteEligibility> {
    return this.httpClient.get<DeleteEligibility>(
      routes.getDeleteEligibility(),
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  requestPermanentDelete(): Observable<void> {
    return this.httpClient.post<void>(
      routes.requestPermanentDelete(),
      {},
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  confirmPermanentDelete(otpCode: string): Observable<void> {
    return this.httpClient.post<void>(
      routes.confirmPermanentDelete(),
      { otpCode },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  changeEmailSendOtp(newEmail: string): Observable<unknown> {
    return this.httpClient.post(
      routes.changeEmailSendOtp(),
      { newEmail },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  changeEmailVerify(
    newEmail: string,
    otp: string,
  ): Observable<ChangeContactVerifyResponse> {
    return this.httpClient.post<ChangeContactVerifyResponse>(
      routes.changeEmailVerify(),
      { newEmail, otp },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  changeEmailConfirm(verificationToken: string): Observable<void> {
    return this.httpClient.post<void>(
      routes.changeEmailConfirm(),
      { verificationToken },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  changePhoneSendOtp(newPhone: string): Observable<unknown> {
    return this.httpClient.post(
      routes.changePhoneSendOtp(),
      { newPhone },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  changePhoneVerify(
    newPhone: string,
    otp: string,
  ): Observable<ChangeContactVerifyResponse> {
    return this.httpClient.post<ChangeContactVerifyResponse>(
      routes.changePhoneVerify(),
      { newPhone, otp },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  changePhoneConfirm(verificationToken: string): Observable<void> {
    return this.httpClient.post<void>(
      routes.changePhoneConfirm(),
      { verificationToken },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }

  changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): Observable<void> {
    return this.httpClient.post<void>(
      routes.changePassword(),
      { currentPassword, newPassword, confirmPassword },
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.credentialsService.credentials.token}`,
          'Cache-Control': 'no-cache',
        }),
      },
    );
  }
}
